// DepartementDetailsPage.jsx
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Popconfirm, Row, Col, Dropdown, Tabs, Card } from "antd";
import {
  EditFilled,
  LockFilled,
  UnlockFilled,
  DownOutlined,
  DeleteFilled,
  ReloadOutlined
} from "@ant-design/icons";
import LoadingPlaceholder from "../../components/utils/LoadingPlaceholder";
import Toast from "../../helpers/Toast";
import PageTitle from "../../components/utils/PageTitle";
import Main from "../../components/layouts/Main";
import { useUserContext } from "../../contexts/UserContext";
import Breadcrumb from "../../components/utils/Breadcrumb";
import DepartementEditForm from "../../components/departement/DepartementEditForm";
import { DepartementService } from "../../services/DepartementService";
import DepartementDetails from "../../components/departement/DepartementDetails";
import { catchError } from "../../services/DaoService";
import PersonnelListByDepartement from "../../components/personnel/PersonnelListByDepartement";
import AffaireListByDepartement from "../../components/affaire/AffaireListByDepartement";

const DepartementDetailsPage = () => {
  const { uid } = useParams();
  const [departement, setDepartement] = useState(null);
  const [loading, setLoading] = useState(true);
  const { check } = useUserContext();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    DepartementService.getInstance()
      .find(uid)
      .then((response) => {
        setDepartement(response.data);
      })
      .catch((error) => {
        catchError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [uid]);
  useEffect(() => {
    load();
  }, [load]);

  function openEditModal() {
    setEditModalVisible(true);
  }

  const handleEnable = () => {
    if (!departement) {
      return;
    }
    DepartementService.getInstance()
      .enable(uid)
      .then((response) => {
        Toast.success(response.message);
        setDepartement(response.data);
      })
      .catch((error) => {
        catchError(error);
      })
  };

  const handleDisable = () => {
    if (!departement) {
      return;
    }
    DepartementService.getInstance()
      .disable(uid)
      .then((response) => {
        Toast.success(response.message);
        setDepartement(response.data);
      })
      .catch((error) => {
        catchError(error);
      })
  };

  const handleDelete = () => {
    DepartementService.getInstance()
      .remove(departement.uid)
      .then(() => {
        navigate("/departement");
      })
      .catch((error) => {
        catchError(error);
      })
  };

  const handleUpdate = (updatedDepartement) => {
    setDepartement(updatedDepartement);
    setEditModalVisible(false);
  };

  const menuItems = [
    {
      key: "1",
      label: (
        <>
          {departement?.enabled ? (
            <Popconfirm
              title="Voulez-vous vraiment désactiver cet élément ?"
              onConfirm={handleDisable}
              okText="Désactiver" description="Vous ne pourrez plus utiliser cette donnée pour de futures enregistrements."
              cancelText="Annuler"
            >
              Désactiver
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Voulez-vous vraiment procéder ?"
              onConfirm={handleEnable}
              okText="Activer"
              cancelText="Annuler"
            >
              Activer
            </Popconfirm>
          )}
        </>
      ),
      danger: true,
      icon: departement?.enabled ? (
        <LockFilled />
      ) : (
        <UnlockFilled title="Activer" />
      ),
      permission: departement?.enabled
        ? "DISABLE-DEPARTEMENT"
        : "ENABLE-DEPARTEMENT",
    },
    {
      key: "2",
      label: (
        <Popconfirm
          title="Voulez-vous vraiment procéder à la suppression ?"
          onConfirm={handleDelete}
          okText="Oui"
          okButtonProps={{ danger: true }}
          cancelText="Non"
        >
          Supprimer
        </Popconfirm>
      ),
      icon: <DeleteFilled />,
      danger: true,
      permission: "DELETE-DEPARTEMENT",
    }
  ];

  const tabItems = [
    {
      key: "affaire",
      label: "Dossiers",
      children: <AffaireListByDepartement departement={departement} />
    },
    {
      key: "personnels",
      label: "Personnels",
      children: <PersonnelListByDepartement departement={departement} />
    }
  ];


  const allowedMenuItems = menuItems.filter((menuItem) =>
    check(menuItem.permission)
  );

  return (
    <Main>
      <Breadcrumb
        parentLink="/departement"
        canSee={check("VIEW-DEPARTEMENTS")}
        parentLinkTitle="Liste des departements"
        title={departement?.nom ?? "En chargement..."}
      />
      <PageTitle
        title={departement ? departement.nom : "departement"}
      >
        <Button
          type="default"
          icon={<ReloadOutlined />}
          onClick={load}
          loading={loading}
        >
          Rafraîchir
        </Button>
        {check("EDIT-DEPARTEMENT") && (
          <Button
            type="default"
            icon={<EditFilled />}
            onClick={openEditModal}
            style={{ backgroundColor: ' #facc15' }}
          >
            Éditer
          </Button>
        )}
        {allowedMenuItems?.length > 0 && (
          <Dropdown
            menu={{ items: allowedMenuItems }}
          >
            <Button type="default" icon={<DownOutlined />}>
                Actions
            </Button>
          </Dropdown>
        )}
      </PageTitle>
      {loading ? (
        <LoadingPlaceholder />
      ) : (
        <Row gutter={[5, 5]}>
          <Col span={24}>
          {departement && <DepartementDetails departement={departement} />}
          </Col>
          <Col span={24}>
            <Card>
              {departement && <Tabs items={tabItems} />}
            </Card>
          </Col>
        </Row>
      )}
      {departement ? <DepartementEditForm
        visible={editModalVisible}
        uid={departement.uid}
        onUpdate={handleUpdate}
        onCancel={() => setEditModalVisible(false)}
      /> : null}
    </Main>
  );
};

export default DepartementDetailsPage;